import React from "react";
import { Badge, Button, DatePicker, InputNumber, Popconfirm, Space, Tag, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { formatMoney } from "../../../utils/util";
import { TagItemTypes } from "../../../components/badge-and-tag/ERP";

import { docLinkRender } from "../../../components/table/DocLink";
const renderEnoughTag = (isEnough) => (
  <Tag color={isEnough ? "success" : "error"}>
    {isEnough ? "พอ" : "ไม่พอ"}
  </Tag>
);

export const accessColumns = ({ handleEdit, handleView, showAction = true }) => {
  const columns = [
    {
      title: "PD Code",
      dataIndex: "pdcode",
      key: "pdcode",
      width: 100,
      sorter: (a, b) => String(a?.pdcode ?? "").localeCompare(String(b?.pdcode ?? "")),
    /* กดเลขที่เอกสารเพื่อเปิดหน้าแสดงข้อมูล */
    render: docLinkRender(handleView, "เปิดดูใบสั่งผลิต"),
  },
    {
      title: "PD Date",
      dataIndex: "pddate",
      key: "pddate",
      width: 110,
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY") : ""),
    },
    {
      title: "สถานะ",
      dataIndex: "doc_status",
      key: "doc_status",
      width: 140,
      render: (value) => value ? <Tag color="processing">{value}</Tag> : "-",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
    },
    {
      title: "Action",
      key: "action",
      width: 125,
      fixed: "right",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", justifyContent: "start" }}>
          {typeof handleView === "function" && (
            <Badge size="small" offset={[0, 1]}>
              <Button
                size="small"
                icon={<ExclamationCircleOutlined />}
                className="bn-success-outline"
                style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                onClick={() => handleView(record)}
              />
            </Badge>
          )}
          {typeof handleEdit === "function" && (
            <Badge size="small" offset={[0, 1]}>
              <Button
                size="small"
                icon={<EditOutlined />}
                className="bn-primary-outline"
                style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                onClick={() => handleEdit(record)}
              />
            </Badge>
          )}
          {/* พิมพ์ใบสั่งงานการผลิต */}
          {!!record?.pdcode && (
            <Badge size="small" offset={[0, 1]}>
              <Button
                size="small"
                title="ใบสั่งงานการผลิต"
                icon={<PrinterOutlined />}
                className="bn-warning-outline"
                style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                onClick={() =>
                  window.open(
                    `/pd-work-order/${encodeURIComponent(record.pdcode)}`,
                    "_blank"
                  )
                }
              />
            </Badge>
          )}
        </div>
      ),
    },
  ];

  return columns;
};

/* หน่วยใน DB เขียนปนกัน (กก./kg/g/กรัม)
   ตารางวัตถุดิบแสดงหน่วยเป็น kg เสมอ ทศนิยม 3 ตำแหน่ง
   ของที่เก็บมาเป็นกรัมจึงต้องหาร 1000 ก่อนแสดง ไม่งั้นเลขเป็นกรัมแต่ป้ายบอก kg */
const KG_UNITS = ["กก", "กก.", "kg", "Kg", "KG", "KGS", "kgs", "กิโลกรัม"];
const GRAM_UNITS = ["g", "G", "gm", "gr", "gram", "grams", "กรัม"];
const normUnit = (v) => (v ?? "").trim();
const isGramUnit = (v) => GRAM_UNITS.includes(normUnit(v));
const isWeightUnit = (v) => KG_UNITS.includes(normUnit(v)) || isGramUnit(v);

/* หน่วยที่โชว์ — ของที่ชั่งน้ำหนักโชว์เป็น kg ส่วนหน่วยนับอื่น (ชิ้น/ใบ) คงเดิม */
const displayUnit = (v) => (isWeightUnit(v) ? "kg" : v);

/* จำนวนที่โชว์ — แปลงกรัมเป็นกิโลกรัมให้ตรงกับหน่วยที่แสดง */
const toKg = (value, unit) =>
  isGramUnit(unit) ? Number(value || 0) / 1000 : Number(value || 0);
const displayQty = (value, unit) =>
  formatMoney(toKg(value, unit), isWeightUnit(unit) ? 3 : 2);

export const requirementColumns = [
  { title: "Code", dataIndex: "mat_code", key: "mat_code", width: 140 },
  { title: "Name", dataIndex: "mat_name", key: "mat_name" },
  {
    title: "Type",
    dataIndex: "typename",
    key: "typename",
    width: 140,
    render: (v) => <TagItemTypes data={v} />,
  },
  {
    title: "Required",
    dataIndex: "required_qty",
    key: "required_qty",
    width: 130,
    align: "right",
    /* หน่วยน้ำหนักใช้ทศนิยม 3 ตำแหน่ง ให้ตรงกับตารางวัตถุดิบ */
    render: (value, record) => displayQty(value, record?.unit),
  },
  {
    title: "Stock",
    dataIndex: "stock",
    key: "stock",
    width: 130,
    align: "right",
    /* หน่วยน้ำหนักใช้ทศนิยม 3 ตำแหน่ง ให้ตรงกับตารางวัตถุดิบ */
    render: (value, record) => displayQty(value, record?.unit),
  },
  {
    title: "Balance",
    dataIndex: "balance",
    key: "balance",
    width: 130,
    align: "right",
    /* หน่วยน้ำหนักใช้ทศนิยม 3 ตำแหน่ง ให้ตรงกับตารางวัตถุดิบ */
    render: (value, record) => displayQty(value, record?.unit),
  },
  { title: "Unit", dataIndex: "unit", key: "unit", width: 90, align: "center", render: displayUnit },
  {
    title: "Status",
    dataIndex: "is_enough",
    key: "is_enough",
    width: 110,
    align: "center",
    render: renderEnoughTag,
  },
];

export const shortageColumns = [
  { title: "Code", dataIndex: "mat_code", key: "mat_code", width: 140 },
  { title: "Name", dataIndex: "mat_name", key: "mat_name" },
  {
    title: "Short",
    key: "short_qty",
    width: 130,
    align: "right",
    render: (_, record) => formatMoney(Math.abs(Number(record?.balance || 0)), 2),
  },
  { title: "Unit", dataIndex: "unit", key: "unit", width: 90, align: "center", render: displayUnit },
];
export const jobOrderRefColumns = ({ onRemoveRecord, isViewMode = false }) => {
  const columns = [
    {
      title: "JO Code",
      dataIndex: "jocode",
      key: "jocode",
      width: 110,
    },
    {
      title: "JO Date",
      dataIndex: "jodate",
      key: "jodate",
      width: 110,
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Customer",
      dataIndex: "cusname",
      key: "cusname",
    },
    {
      title: "PG",
      dataIndex: "pg_code",
      key: "pg_code",
      width: 120,
    },
    {
      title: "FG",
      dataIndex: "fg_code",
      key: "fg_code",
      width: 120,
    },
    {
      title: "Ordered Qty",
      key: "ordered_qty",
      width: 130,
      align: "right",
      render: (_, record) => {
        const qty =
          Number(record?.total_unit || 0) ||
          Number(record?.carton || 0) * Number(record?.unit_cs || 0);
        return formatMoney(qty, 0);
      },
    },
  ];

  if (!isViewMode && typeof onRemoveRecord === "function") {
    columns.push({
      title: "",
      key: "action",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onRemoveRecord(record)}
        />
      ),
    });
  }

  return columns;
};

export const selectedJobOrderColumns = ({
  getDefaultQty,
  onQtyChange,
  onPreviewRecord,
  onRemoveRecord,
  activeDetailCode,
}) => [
    {
      title: "JO Code",
      dataIndex: "jocode",
      key: "jocode",
      width: 110,
    },
    {
      title: "Customer",
      dataIndex: "cusname",
      key: "cusname",
    },
    {
      title: "PG",
      dataIndex: "pg_code",
      key: "pg_code",
      width: 120,
    },
    {
      title: "FG",
      dataIndex: "fg_code",
      key: "fg_code",
      width: 120,
    },
    {
      title: "Batches",
      dataIndex: "batch_count",
      key: "batch_count",
      width: 100,
      align: "center",
      render: (value) => value || 1,
    },
    {
      title: "Qty",
      key: "total_unit",
      width: 180,
      render: (_, record) =>
        typeof onQtyChange === "function" ? (
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={getDefaultQty(record)}
            onChange={(value) => onQtyChange(record, Number(value || 0))}
          />
        ) : (
          <span>{getDefaultQty(record)}</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: typeof onRemoveRecord === "function" ? 170 : 110,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type={record?.detail_code === activeDetailCode ? "primary" : "default"}
            onClick={() => onPreviewRecord(record)}
          >
            ดูรายการนี้
          </Button>
          {typeof onRemoveRecord === "function" && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onRemoveRecord(record)}
            />
          )}
        </Space>
      ),
    },
  ];

export const batchPlanningColumns = ({ getBatchSauceWeight, onWeightChange, onDelete, isViewMode = false }) => [
  {
    title: "Batch",
    dataIndex: "batch_no",
    key: "batch_no",
    align: "left",
    render: (value) => `Batch ${value}`,
  },
  {
    title: "วันที่ผลิต",
    dataIndex: "production_date",
    key: "production_date",
    width: 140,
    align: "center",
    render: (value) =>
      value ? dayjs(value).format("DD/MM/YYYY") : <span style={{ color: "#bbb" }}>-</span>,
  },
  {
    title: "น้ำหนัก (kg)",
    dataIndex: "sauce_weight",
    key: "sauce_weight",
    width: 150,
    align: "right",
    render: (_, record) => {
      const weight = Number(getBatchSauceWeight?.(record) || 0);
      if (!isViewMode && typeof onWeightChange === "function") {
        return (
          <InputNumber
            min={0}
            step={0.01}
            style={{ width: "100%" }}
            value={weight}
            onChange={(value) => onWeightChange(record, Number(value || 0))}
          />
        );
      }
      return formatMoney(weight, 2);
    },
  },
  /* ลบหม้อเคี่ยวทีละหม้อ — หม้อที่เหลือจะเรียงเลขใหม่ให้อัตโนมัติ */
  {
    title: "",
    key: "batch_action",
    width: 60,
    align: "center",
    render: (_, record) =>
      isViewMode || typeof onDelete !== "function" ? null : (
        <Popconfirm
          title="ลบหม้อเคี่ยวนี้"
          description={`ต้องการลบ Batch ${record?.batch_no} ใช่หรือไม่?`}
          okText="ลบ"
          cancelText="ปิด"
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(record)}
        >
          <Tooltip title="ลบหม้อเคี่ยว">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      ),
  },
];

export const totalMaterialColumns = (onQtyChange) => [
  { title: "Code", dataIndex: "mat_code", key: "mat_code", width: 140 },
  { title: "Name", dataIndex: "mat_name", key: "mat_name" },
  {
    title: "Type",
    dataIndex: "typename",
    key: "typename",
    width: 140,
    render: (v) => <TagItemTypes data={v} />,
  },
  {
    title: "จำนวนรวม",
    dataIndex: "required_qty",
    key: "required_qty",
    width: 160,
    align: "right",
    render: (v, record) => {
      const weight = isWeightUnit(record?.unit);
      return onQtyChange ? (
        /* แสดง/แก้ไขเป็นกิโลกรัม แต่เก็บกลับเป็นหน่วยเดิมของวัตถุดิบ */
        <InputNumber
          min={0}
          step={weight ? 0.001 : 0.01}
          style={{ width: "100%" }}
          value={toKg(v, record?.unit)}
          formatter={(val) => formatMoney(Number(val || 0), weight ? 3 : 2)}
          onChange={(val) =>
            onQtyChange(
              record.mat_code,
              isGramUnit(record?.unit) ? Number(val || 0) * 1000 : val
            )
          }
        />
      ) : (
        displayQty(v, record?.unit)
      );
    },
  },
  { title: "Unit", dataIndex: "unit", key: "unit", width: 80, align: "center", render: displayUnit },
];

export const batchMaterialColumns = [
  { title: "Code", dataIndex: "mat_code", key: "mat_code", width: 140 },
  { title: "Name", dataIndex: "mat_name", key: "mat_name" },
  {
    title: "Type",
    dataIndex: "typename",
    key: "typename",
    width: 140,
    render: (v) => <TagItemTypes data={v} />,
  },
  {
    title: "จำนวน / Batch",
    dataIndex: "batch_qty",
    key: "batch_qty",
    width: 130,
    align: "right",
    render: (v, record) => displayQty(v, record?.unit),
  },
  { title: "Unit", dataIndex: "unit", key: "unit", width: 80, align: "center", render: displayUnit },
];

const MP_GRAM_UNITS = ["g", "gm", "gram", "grams", "กรัม"];
const mpRatioKg = (r) => {
  const v = Number(r?.ratio || 0);
  return MP_GRAM_UNITS.includes(String(r?.unit || "").trim().toLowerCase()) ? v / 1000 : v;
};
export const masterProcessColumns = (productionQty = 0, rows = []) => {
  const totalRatio = (rows || []).reduce((a, r) => a + mpRatioKg(r), 0);
  return [
  {
    title: "Step",
    dataIndex: "displayStepNo",
    key: "displayStepNo",
    width: 60,
    align: "center",
  },
  {
    title: "Code",
    dataIndex: "stcode",
    key: "stcode",
    width: 140,
  },
  {
    title: "Name",
    dataIndex: "stname",
    key: "stname",
  },
  {
    title: "ประเภท",
    dataIndex: "sttype",
    key: "sttype",
    width: 140,
    render: (value) => <TagItemTypes data={value} />,
  },
  {
    title: "%",
    key: "percent",
    width: 110,
    align: "right",
    render: (_, record) => {
      const pct = totalRatio > 0 ? (mpRatioKg(record) / totalRatio) * 100 : 0;
      return pct > 0 ? formatMoney(pct, 2) : "-";
    },
  },
  {
    title: "รวมที่ต้องใช้",
    key: "total_required",
    width: 140,
    align: "right",
    render: (_, record) => {
      const pct = totalRatio > 0 ? mpRatioKg(record) / totalRatio : 0;
      const kg = Number(productionQty || 0) * pct;
      const isGram = MP_GRAM_UNITS.includes(String(record?.unit || "").trim().toLowerCase());
      const total = parseFloat((isGram ? kg * 1000 : kg).toFixed(4));
      return total > 0 ? (
        <span style={{ fontWeight: 600, color: "#1677ff" }}>
          {formatMoney(total, 4)}
        </span>
      ) : "-";
    },
  },
  {
    title: "Unit",
    dataIndex: "unit",
    key: "unit",
    width: 90,
    align: "center",
    render: displayUnit,
  },
  {
    title: "ขั้นตอน",
    dataIndex: "method",
    key: "method",
    width: 220,
    render: (value) => (
      <Tooltip title={value || ""}>
        <span>{value || "-"}</span>
      </Tooltip>
    ),
  },
];
};

export const orderColumns = [
  { title: "No.", dataIndex: "seq", key: "seq", width: 60, align: "center" ,render: (_, __, index) => index + 1  },
  { title: "JO Code", dataIndex: "jocode", key: "jocode", width: 110 },
  {
    title: "JO Date",
    dataIndex: "jodate",
    key: "jodate",
    width: 110,
    render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-"),
  },
  { title: "Customer", dataIndex: "cusname", key: "cusname" },
  { title: "Brand", dataIndex: "brand", key: "brand", width: 120 },
  { title: "PG Code", dataIndex: "pg_code", key: "pg_code", width: 120 },
  { title: "FG Code", dataIndex: "fg_code", key: "fg_code", width: 120 },
  { title: "PG Name", dataIndex: "pg_name", key: "pg_name" },
  { title: "FG Name", dataIndex: "fg_name", key: "fg_name" },
  {
    title: "Batch",
    dataIndex: "batch_count",
    key: "batch_count",
    width: 80,
    align: "center",
    render: (v) => v || 1,
  },
  {
    title: "Prod Qty",
    dataIndex: "qty",
    key: "qty",
    width: 110,
    align: "right",
    render: (v) => formatMoney(Number(v || 0), 2),
  },
  {
    title: "Total Unit",
    dataIndex: "total_unit",
    key: "total_unit",
    width: 100,
    align: "right",
    render: (v) => formatMoney(Number(v || 0), 0),
  },
  {
    title: "Carton",
    dataIndex: "carton",
    key: "carton",
    width: 90,
    align: "right",
    render: (v) => formatMoney(Number(v || 0), 0),
  },
  {
    title: "Unit/cs",
    dataIndex: "unit_cs",
    key: "unit_cs",
    width: 90,
    align: "right",
    render: (v) => formatMoney(Number(v || 0), 0),
  },
  {
    title: "Net wt (gm)",
    dataIndex: "net_weight",
    key: "net_weight",
    width: 110,
    align: "right",
    render: (v) => formatMoney(Number(v || 0), 2),
  },
  {
    title: "น้ำหนักซอส (kg)",
    dataIndex: "sauce_weight",
    key: "sauce_weight",
    width: 130,
    align: "right",
    render: (v) => formatMoney(Number(v || 0), 2),
  },
  {
    title: "ส่วนสูญเสีย (%)",
    dataIndex: "lost",
    key: "lost",
    width: 130,
    align: "right",
    render: (v) => formatMoney(Number(v || 0), 2),
  },
  {
    title: "สถานะ",
    dataIndex: "production_status",
    key: "production_status",
    width: 120,
    align: "center",
    render: (v) => (v ? <Tag color="processing">{v}</Tag> : "-"),
  },  
  { title: "Remark", dataIndex: "detail_remark", key: "detail_remark" },
];